import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, LogOut, ShieldCheck, MapPin, Edit3, X, Upload, Crown } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import Logo from "../components/Logo";
import { toast } from "sonner";

export default function Profile() {
    const { user, refresh, logout } = useAuth();
    const nav = useNavigate();
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({
        name: user?.name || "",
        bio: user?.bio || "",
        location_city: user?.location_city || "",
        age: user?.age || "",
    });
    const [busy, setBusy] = useState(false);
    const photoInput = useRef(null);
    const verifyInput = useRef(null);

    if (!user) return null;

    const upload = async (file, kind) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        setBusy(true);
        try {
            await api.post("/photos/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await refresh();
            toast.success("Uploaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const removePhoto = async (url) => {
        await api.delete(`/photos?url=${encodeURIComponent(url)}`);
        await refresh();
    };

    const saveProfile = async () => {
        setBusy(true);
        try {
            await api.put("/profile/update", {
                name: form.name,
                bio: form.bio,
                location_city: form.location_city,
                age: form.age ? Number(form.age) : undefined,
            });
            await refresh();
            toast.success("Saved");
            setEditing(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        nav("/");
    };

    return (
        <div className="min-h-screen pb-24">
            <header className="px-5 pt-5 pb-3 flex items-center justify-between">
                <Logo size="sm" />
                <button
                    onClick={handleLogout}
                    data-testid="profile-logout-btn"
                    className="text-white/60 hover:text-smash text-xs uppercase tracking-widest flex items-center gap-1"
                >
                    <LogOut className="w-3.5 h-3.5" /> Exit
                </button>
            </header>

            {/* Header photo */}
            <div className="relative w-full aspect-[4/5] bg-ink-800">
                {user.photos?.[0] ? (
                    <img src={fileUrl(user.photos[0])} alt="" className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center font-display text-7xl text-white/20">
                        {user.name?.[0] || "?"}
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="flex items-end gap-2">
                        <h1 className="font-display text-4xl leading-none">{user.name?.toUpperCase()}</h1>
                        <span className="font-display text-2xl text-white/70">{user.age}</span>
                        {user.verified && <ShieldCheck className="w-5 h-5 text-dash mb-1" />}
                        {user.premium && <Crown className="w-5 h-5 text-dash mb-1" />}
                    </div>
                    {user.location_city && (
                        <div className="text-xs uppercase tracking-widest text-white/70 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {user.location_city}
                        </div>
                    )}
                </div>
            </div>

            <div className="px-5 pt-5 space-y-6">
                {/* Premium banner */}
                <button
                    onClick={() => nav("/premium")}
                    data-testid="profile-premium-cta"
                    className={`w-full border-2 p-4 text-left transition-colors ${
                        user.premium
                            ? "border-dash bg-dash/10"
                            : "border-smash/70 bg-smash/10 hover:bg-smash/20"
                    }`}
                >
                    <div className="flex items-center gap-2 mb-1">
                        <Crown className={`w-4 h-4 ${user.premium ? "text-dash" : "text-smash"}`} />
                        <span className={`text-[10px] uppercase tracking-[0.3em] font-semibold ${user.premium ? "text-dash" : "text-smash"}`}>
                            {user.premium ? "Smash+ Active" : "Smash+ Premium"}
                        </span>
                    </div>
                    <div className="font-display text-xl text-white">
                        {user.premium ? "UNLIMITED UNLOCKED." : "UPGRADE — $15/MO"}
                    </div>
                    <div className="text-xs text-white/60 mt-0.5">
                        {user.premium
                            ? (user.premium_until ? `Active until ${new Date(user.premium_until).toLocaleDateString()}` : "Active")
                            : "Unlimited swipes. Message anyone you swipe."}
                    </div>
                </button>

                <div className="flex items-center justify-between">
                    <h2 className="font-display text-xl text-white">DETAILS</h2>
                    <button
                        onClick={() => setEditing((v) => !v)}
                        data-testid="profile-edit-toggle-btn"
                        className="text-xs uppercase tracking-widest text-dash flex items-center gap-1 hover:underline"
                    >
                        <Edit3 className="w-3 h-3" /> {editing ? "Cancel" : "Edit"}
                    </button>
                </div>

                {editing ? (
                    <div className="space-y-3" data-testid="profile-edit-form">
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Name"
                            data-testid="profile-name-input"
                            className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 outline-none text-white"
                        />
                        <input
                            type="number"
                            value={form.age}
                            onChange={(e) => setForm({ ...form, age: e.target.value })}
                            placeholder="Age"
                            min="18"
                            data-testid="profile-age-input"
                            className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 outline-none text-white"
                        />
                        <input
                            value={form.location_city}
                            onChange={(e) => setForm({ ...form, location_city: e.target.value })}
                            placeholder="City"
                            data-testid="profile-city-input"
                            className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 outline-none text-white"
                        />
                        <textarea
                            value={form.bio}
                            onChange={(e) => setForm({ ...form, bio: e.target.value })}
                            placeholder="Bio"
                            maxLength={280}
                            rows={4}
                            data-testid="profile-bio-input"
                            className="w-full bg-ink-800 border border-white/10 focus:border-smash p-3 outline-none text-white resize-none"
                        />
                        <button
                            onClick={saveProfile}
                            disabled={busy}
                            data-testid="profile-save-btn"
                            className="w-full h-12 bg-smash hover:bg-smash/90 disabled:opacity-50 text-white font-display uppercase tracking-widest"
                        >
                            Save
                        </button>
                    </div>
                ) : (
                    <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">
                        {user.bio || <span className="text-white/40 italic">No bio yet.</span>}
                    </p>
                )}

                <div>
                    <h2 className="font-display text-xl text-white mb-3">PHOTOS</h2>
                    <div className="grid grid-cols-3 gap-2" data-testid="profile-photos-grid">
                        {(user.photos || []).map((p, i) => (
                            <div key={p} className="aspect-square relative group border border-white/10" data-testid={`profile-photo-${i}`}>
                                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                                <button
                                    onClick={() => removePhoto(p)}
                                    data-testid={`profile-remove-photo-${i}`}
                                    className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white flex items-center justify-center hover:bg-smash"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                        {(user.photos?.length || 0) < 6 && (
                            <button
                                onClick={() => photoInput.current?.click()}
                                disabled={busy}
                                data-testid="profile-add-photo-btn"
                                className="aspect-square border border-dashed border-white/20 hover:border-smash flex items-center justify-center text-white/50 hover:text-smash"
                            >
                                <Camera className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                    <input
                        ref={photoInput}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => upload(e.target.files?.[0], "profile")}
                        data-testid="profile-photo-file-input"
                    />
                </div>

                <div className="border border-dash/40 bg-dash/5 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="w-4 h-4 text-dash" />
                        <span className="text-[10px] uppercase tracking-[0.25em] text-dash">Verification</span>
                    </div>
                    {user.verified ? (
                        <p className="text-sm text-dash">You are verified.</p>
                    ) : user.verification_photo ? (
                        <p className="text-xs text-white/70">
                            Verification pending review.
                        </p>
                    ) : (
                        <>
                            <p className="text-xs text-white/70 leading-relaxed">
                                Upload a selfie to earn a verified badge.
                            </p>
                            <button
                                onClick={() => verifyInput.current?.click()}
                                data-testid="profile-verify-btn"
                                className="mt-3 h-10 px-4 bg-dash text-black font-display text-xs uppercase tracking-widest hover:bg-dash/90"
                            >
                                <Upload className="inline w-3 h-3 mr-1" /> Upload Selfie
                            </button>
                            <input
                                ref={verifyInput}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => upload(e.target.files?.[0], "verification")}
                                data-testid="profile-verify-file-input"
                            />
                        </>
                    )}
                </div>

                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 text-center pt-4">
                    Smash & Dash — use at your own risk
                </div>
            </div>
            <BottomNav />
        </div>
    );
}
